import React, { useState, useRef, useCallback, useEffect } from 'react';
import Monaco from '@monaco-editor/react';
import { Modal, Input, message, Dropdown, Tooltip, Space } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  CopyOutlined,
  ScissorOutlined,
  FileAddOutlined,
  FolderAddOutlined,
} from '@ant-design/icons';
import FileTree from './FileTree';
import TabBar from './TabBar';
import { FileNode, OpenFile, ContextMenuData } from '../../types/types';
import './CodeEditor.css';
import { ItemType } from 'antd/es/menu/interface';
import { useSession } from '../../contexts/SessionContext';

const getLanguageFromExtension = (filename: string): string => {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const languageMap: Record<string, string> = {
    js: 'javascript',
    ts: 'typescript',
    jsx: 'javascript',
    tsx: 'typescript',
    html: 'html',
    css: 'css',
    scss: 'scss',
    json: 'json',
    md: 'markdown',
    py: 'python',
    java: 'java',
    c: 'c',
    cpp: 'cpp',
    cs: 'csharp',
    php: 'php',
    go: 'go',
    rs: 'rust',
    sql: 'sql',
    yaml: 'yaml',
    yml: 'yaml',
  };

  return languageMap[ext] || 'plaintext';
};

const findChildByName = (fs: Map<string, FileNode>, parentId: string, name: string): FileNode | null => {
  const parent = fs.get(parentId);
  if (!parent?.children) return null;

  for (const [_, child] of parent.children) {
    if (child.name === name) return child;
  }
  return null;
};

const createFile = (fs: Map<string, FileNode>, parentId: string, name: string, content = ''): FileNode | null => {
  const parent = fs.get(parentId);

  if (!parent || parent.type !== 'folder') return null;

  const fullPath = `${parent.fullPath}${name}`;
  const id = fullPath;

  const file: FileNode = {
    id,
    name,
    fullPath,
    type: 'file',
    content,
    parent: parentId,
    modified: false,
    language: getLanguageFromExtension(name),
  };

  fs.set(id, file);
  parent.children!.set(id, file);

  return file;
};

const createFolder = (fs: Map<string, FileNode>, parentId: string, name: string): FileNode | null => {
  const parent = fs.get(parentId);

  if (!parent || parent.type !== 'folder') return null;

  const fullPath = `${parent.fullPath}${name}/`;
  const id = fullPath;

  const folder: FileNode = {
    id,
    name,
    fullPath,
    type: 'folder',
    children: new Map(),
    parent: parentId,
    expanded: false,
  };

  fs.set(id, folder);
  parent.children!.set(id, folder);

  return folder;
};

const createFileFromPath = (fs: Map<string, FileNode>, path: string, content: string) => {
  const parts = path.split('/').filter(Boolean);
  let currentParentId = 'root';

  // 创建路径中的所有文件夹
  for (let i = 0; i < parts.length - 1; i++) {
    const folderName = parts[i];
    const existing = findChildByName(fs, currentParentId, folderName);

    if (existing) {
      currentParentId = existing.id;
    } else {
      const newFolder = createFolder(fs, currentParentId, folderName);
      if (newFolder) {
        currentParentId = newFolder.id;
      }
    }
  }

  // 创建文件
  const fileName = parts[parts.length - 1];
  createFile(fs, currentParentId, fileName, content);
};

const cloneItem = (item: FileNode): FileNode => {
  const clone: FileNode = {
    id: item.id,
    name: item.name,
    fullPath: item.fullPath,
    type: item.type,
    content: item.content,
    children: new Map(),
  };

  if (item.type === 'folder' && item.children) {
    for (const [_, child] of item.children) {
      const childClone = cloneItem(child);
      clone.children!.set(childClone.id, childClone);
    }
  }

  return clone;
};

const nameExistsInParent = (fs: Map<string, FileNode>, name: string, parentId: string): boolean => {
  const parent = fs.get(parentId);
  if (!parent?.children) return false;

  for (const [_, child] of parent.children) {
    if (child.name === name) return true;
  }
  return false;
};

const getUniqueNameInParent = (fs: Map<string, FileNode>, baseName: string, parentId: string): string => {
  const parent = fs.get(parentId);
  if (!parent?.children) return baseName;

  let counter = 1;
  let newName = baseName;

  while (nameExistsInParent(fs, newName, parentId)) {
    const extension = baseName.includes('.') ? baseName.substring(baseName.lastIndexOf('.')) : '';
    const nameWithoutExt = baseName.includes('.') ? baseName.substring(0, baseName.lastIndexOf('.')) : baseName;
    newName = `${nameWithoutExt}_copy${counter > 1 ? counter : ''}${extension}`;
    counter++;
  }

  return newName;
};

const pasteItemRecursive = (fs: Map<string, FileNode>, item: FileNode, parentId: string) => {
  const newName = getUniqueNameInParent(fs, item.name, parentId);

  if (item.type === 'file') {
    createFile(fs, parentId, newName, item.content || '');
  } else if (item.children) {
    const newFolder = createFolder(fs, parentId, newName);
    if (newFolder) {
      for (const [_, child] of item.children) {
        pasteItemRecursive(fs, child, newFolder.id);
      }
    }
  }
};

const deleteItemRecursive = (fs: Map<string, FileNode>, id: string) => {
  const item = fs.get(id);
  if (!item) return;

  if (item.type === 'folder' && item.children) {
    for (const [childId] of item.children) {
      deleteItemRecursive(fs, childId);
    }
  }

  fs.delete(id);
};

const CodeEditor: React.FC = () => {
  const { files: initialFiles, setFile } = useSession();
  const [fileSystem, setFileSystem] = useState<Map<string, FileNode>>(new Map());
  const [openFiles, setOpenFiles] = useState<Map<string, OpenFile>>(new Map());
  const [activeFileId, setActiveFileId] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<ContextMenuData>({
    x: 0,
    y: 0,
    targetId: '',
    show: false,
  });
  const [clipboard, setClipboard] = useState<FileNode | null>(null);
  const [filePanelWidth, setFilePanelWidth] = useState(300);

  // 新增状态用于管理Modal
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [createModalType, setCreateModalType] = useState<'file' | 'folder'>('file');
  const [createModalParentId, setCreateModalParentId] = useState('root');
  const [createModalName, setCreateModalName] = useState('');

  const [renameModalVisible, setRenameModalVisible] = useState(false);
  const [renameModalId, setRenameModalId] = useState('');
  const [renameModalName, setRenameModalName] = useState('');

  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteModalId, setDeleteModalId] = useState('');
  const [deleteModalType, setDeleteModalType] = useState<'file' | 'folder'>('file');
  const [deleteModalName, setDeleteModalName] = useState('');

  const editorRef = useRef<any>(null);
  const monacoRef = useRef<any>(null);
  const isResizing = useRef(false);

  // 初始化文件系统
  useEffect(() => {
    const rootFolder: FileNode = {
      id: 'root',
      name: 'root',
      fullPath: '/',
      type: 'folder',
      children: new Map(),
      expanded: true,
    };

    const newFileSystem = new Map<string, FileNode>();
    newFileSystem.set('root', rootFolder);

    // 如果有初始文件，创建它们
    Object.entries(initialFiles).forEach(([path, content]) => {
      createFileFromPath(newFileSystem, path, content);
    });

    // 如果没有初始文件，创建示例文件
    if (Object.keys(initialFiles).length === 0) {
      createFile(newFileSystem, 'root', 'index.js', 'console.log("Hello World!");');
      const srcFolder = createFolder(newFileSystem, 'root', 'src');
      if (srcFolder) {
        createFile(
          newFileSystem,
          srcFolder.id,
          'app.js',
          'export default function App() {\n  return "Hello from App";\n}'
        );
        createFile(
          newFileSystem,
          srcFolder.id,
          'utils.js',
          'export const utils = {\n  formatDate: (date) => {\n    return date.toLocaleDateString();\n  }\n};'
        );
      }
    }

    setFileSystem(newFileSystem);
  }, []);

  const deleteItem = useCallback(
    (id: string) => {
      setFileSystem(prev => {
        const newFs = new Map(prev);
        const item = newFs.get(id);

        if (!item || id === 'root') return prev;

        // 递归删除子项
        if (item.type === 'folder' && item.children) {
          for (const [childId] of item.children) {
            deleteItemRecursive(newFs, childId);
          }
        }

        // 如果是已打开的文件，关闭它
        if (item.type === 'file') {
          setOpenFiles(prevOpen => {
            const newOpen = new Map(prevOpen);
            const openFile = newOpen.get(id);
            if (openFile) {
              openFile.model?.dispose();
              newOpen.delete(id);
            }
            return newOpen;
          });

          if (activeFileId === id) {
            setActiveFileId(null);
          }
        }

        // 从父级移除
        const parent = newFs.get(item.parent!);
        if (parent?.children) {
          parent.children.delete(id);
        }

        newFs.delete(id);
        return newFs;
      });
    },
    [activeFileId]
  );

  const renameItem = useCallback(
    (id: string, newName: string): boolean => {
      if (!newName.trim() || id === 'root') return false;

      let success = true;

      setFileSystem(prev => {
        const newFs = new Map(prev);
        const item = newFs.get(id);
        if (!item) return prev;

        // 检查同级是否有重名
        const parent = newFs.get(item.parent!);
        if (parent?.children) {
          for (const [_, sibling] of parent.children) {
            if (sibling.id !== id && sibling.name === newName) {
              message.error('该名称已存在');
              success = false;
              return prev;
            }
          }
        }

        item.name = newName;

        if (item.type === 'file') {
          item.language = getLanguageFromExtension(newName);

          // 更新已打开文件的语言
          const openFile = openFiles.get(id);
          if (openFile && monacoRef.current) {
            monacoRef.current.editor.setModelLanguage(openFile.model, item.language);
          }
        }

        return newFs;
      });

      return success;
    },
    [openFiles]
  );

  const copyItem = useCallback(
    (id: string) => {
      const item = fileSystem.get(id);
      if (item && id !== 'root') {
        setClipboard(cloneItem(item));
      }
    },
    [fileSystem]
  );

  const pasteItem = useCallback(
    (parentId: string) => {
      if (!clipboard) return;

      setFileSystem(prev => {
        const newFs = new Map(prev);
        const parent = newFs.get(parentId);

        if (!parent || parent.type !== 'folder') return prev;

        pasteItemRecursive(newFs, clipboard, parentId);
        return newFs;
      });
    },
    [clipboard]
  );

  const openFile = useCallback(
    (id: string) => {
      const file = fileSystem.get(id);
      if (!file || file.type !== 'file') return;

      // 如果文件已打开，切换到该标签
      if (openFiles.has(id)) {
        setActiveFileId(id);
        return;
      }

      // 创建新的打开文件
      setOpenFiles(prev => {
        const newOpen = new Map(prev);

        // Monaco 模型将在编辑器加载后创建
        const openFile: OpenFile = {
          id,
          model: null,
          modified: false,
          viewState: null,
        };

        newOpen.set(id, openFile);
        return newOpen;
      });

      setActiveFileId(id);
    },
    [fileSystem, openFiles]
  );

  const closeFile = useCallback(
    (id: string) => {
      const openFile = openFiles.get(id);
      if (!openFile) return;

      // 检查未保存的修改
      if (openFile.modified) {
        const file = fileSystem.get(id);
        if (!window.confirm(`"${file?.name}" 有未保存的更改，确定要关闭吗？`)) {
          return;
        }
      }

      // 释放模型和更新状态
      setOpenFiles(prev => {
        const newOpen = new Map(prev);
        const file = newOpen.get(id);
        if (file?.model) {
          file.model.dispose();
        }
        newOpen.delete(id);
        return newOpen;
      });

      // 如果关闭的是当前活动文件，切换到其他文件
      if (activeFileId === id) {
        const remainingFiles = Array.from(openFiles.keys()).filter(fId => fId !== id);
        setActiveFileId(remainingFiles.length > 0 ? remainingFiles[remainingFiles.length - 1] : null);
      }
    },
    [openFiles, fileSystem, activeFileId]
  );

  const saveCurrentFile = useCallback(
    (fileId: string, content: string) => {
      setFileSystem(prev => {
        const newFs = new Map(prev);
        const file = newFs.get(fileId);
        if (file) {
          file.content = content;
        }
        if (setFile) {
          setFile(newFs.get(fileId)?.fullPath || '', content);
        }
        return newFs;
      });

      setOpenFiles(prev => {
        const newOpen = new Map(prev);
        const openFile = newOpen.get(fileId);
        if (openFile) {
          openFile.modified = false;
        }
        return newOpen;
      });
      
    },
    [setFile]
  );

  const updateEditorModel = useCallback(
    (fileId: string) => {
      if (!editorRef.current || !monacoRef.current) return;

      const file = fileSystem.get(fileId);
      if (!file || file.type !== 'file') return;

      let openFile = openFiles.get(fileId);

      if (!openFile) {
        return;
      }

      if (!openFile?.model) {
        // 创建新模型
        const model = monacoRef.current.editor.createModel(
          file.content || '',
          file.language,
          monacoRef.current.Uri.file(file.fullPath)
        );

        model._fileId = fileId;

        setOpenFiles(prev => {
          const newOpen = new Map(prev);
          const existing = newOpen.get(fileId);
          if (!existing) return prev;
          existing.model = model;
          newOpen.set(fileId, existing);
          return newOpen;
        });

        openFile = { ...openFile, model };
      }

      // 保存当前视图状态
      const currentModel = editorRef.current.getModel();
      if (currentModel && activeFileId && activeFileId !== fileId) {
        const currentOpenFile = openFiles.get(activeFileId);
        if (currentOpenFile) {
          currentOpenFile.viewState = editorRef.current.saveViewState();
        }
      }

      // 设置新模型
      editorRef.current.setModel(openFile.model);

      // 恢复视图状态
      if (openFile.viewState) {
        editorRef.current.restoreViewState(openFile.viewState);
      }

      editorRef.current.focus();
    },
    [fileSystem, openFiles, activeFileId]
  );

  // 修改 handleEditorMount 函数
  const handleEditorMount = (editor: any, monaco: any) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // 设置保存快捷键
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      const content = editor.getValue();
      const fileId = editor.getModel()._fileId;
      saveCurrentFile(fileId, content);
    });

    // 监听内容变化
    editor.onDidChangeModelContent(() => {
      const fileId = editor.getModel()._fileId;
      if (fileId) {
        const currentContent = editor.getValue();
        const originalContent = fileSystem.get(fileId)?.content || '';

        setOpenFiles(prev => {
          const newOpen = new Map(prev);
          const openFile = newOpen.get(fileId);
          if (openFile) {
            // 比较当前内容与原始内容是否相同
            openFile.modified = currentContent !== originalContent;
          }
          return newOpen;
        });
      }
    });

    if (activeFileId) {
      updateEditorModel(activeFileId);
    }
  };

  // 当活动文件改变时更新编辑器
  useEffect(() => {
    if (activeFileId) {
      updateEditorModel(activeFileId);
    } else if (editorRef.current) {
      editorRef.current = null;
    }
  }, [activeFileId, updateEditorModel]);

  const toggleFolder = useCallback((id: string) => {
    setFileSystem(prev => {
      const newFs = new Map(prev);
      const folder = newFs.get(id);
      if (folder && folder.type === 'folder') {
        folder.expanded = !folder.expanded;
      }
      return newFs;
    });
  }, []);

  // 显示创建文件/文件夹的Modal
  const showCreateModal = (type: 'file' | 'folder', parentId: string) => {
    setCreateModalType(type);
    setCreateModalParentId(parentId);
    setCreateModalName('');
    setCreateModalVisible(true);
  };

  // 处理创建文件/文件夹
  const handleCreateItem = () => {
    if (!createModalName.trim()) {
      message.error('名称不能为空');
      return;
    }

    if (nameExistsInParent(fileSystem, createModalName, createModalParentId)) {
      message.error('该名称已存在');
      return;
    }

    setFileSystem(prev => {
      const newFs = new Map(prev);

      if (createModalType === 'file') {
        const file = createFile(newFs, createModalParentId, createModalName);
        if (file) {
          // 自动打开新创建的文件
          setTimeout(() => openFile(file.id), 0);
        }
      } else {
        createFolder(newFs, createModalParentId, createModalName);
      }

      // 展开父文件夹
      const parent = newFs.get(createModalParentId);
      if (parent && parent.type === 'folder') {
        parent.expanded = true;
      }

      return newFs;
    });

    setCreateModalVisible(false);
    message.success(`${createModalType === 'file' ? '文件' : '文件夹'}创建成功`);
  };

  // 显示重命名Modal
  const showRenameModal = (id: string) => {
    const item = fileSystem.get(id);
    if (!item) return;

    setRenameModalId(id);
    setRenameModalName(item.name);
    setRenameModalVisible(true);
  };

  // 处理重命名
  const handleRename = () => {
    if (!renameModalName.trim()) {
      message.error('名称不能为空');
      return;
    }

    const item = fileSystem.get(renameModalId);
    if (!item) return;

    if (renameModalName === item.name) {
      setRenameModalVisible(false);
      return;
    }

    const success = renameItem(renameModalId, renameModalName);
    if (success) {
      setRenameModalVisible(false);
      message.success('重命名成功');
    }
  };

  // 显示删除确认Modal
  const showDeleteModal = (id: string) => {
    const item = fileSystem.get(id);
    if (!item) return;

    setDeleteModalId(id);
    setDeleteModalType(item.type);
    setDeleteModalName(item.name);
    setDeleteModalVisible(true);
  };

  // 处理删除
  const handleDelete = () => {
    deleteItem(deleteModalId);
    setDeleteModalVisible(false);
    message.success(`${deleteModalType === 'file' ? '文件' : '文件夹'}已删除`);
  };

  const handleFileTreeAction = useCallback(
    (action: string, targetId?: string) => {
      const target = targetId || 'root';

      switch (action) {
        case 'newFile':
          showCreateModal('file', target);
          break;
        case 'newFolder':
          showCreateModal('folder', target);
          break;
        case 'rename':
          if (target !== 'root') {
            showRenameModal(target);
          }
          break;
        case 'copy':
          if (target !== 'root') {
            copyItem(target);
            message.success('已复制到剪贴板');
          }
          break;
        case 'paste':
          pasteItem(target);
          message.success('粘贴成功');
          break;
        case 'delete':
          if (target !== 'root') {
            showDeleteModal(target);
          }
          break;
      }

      setContextMenu(prev => ({ ...prev, show: false }));
    },
    [copyItem, pasteItem]
  );

  const handleContextMenu = useCallback((e: React.MouseEvent, targetId: string) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      targetId,
      show: true,
    });
  }, []);

  const hideContextMenu = useCallback(() => {
    setContextMenu(prev => ({ ...prev, show: false }));
  }, []);

  // 处理面板大小调整
  const handleMouseDown = () => {
    isResizing.current = true;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;

      const newWidth = Math.max(200, Math.min(e.clientX, window.innerWidth - 200));
      setFilePanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      if (activeFileId) {
        updateEditorModel(activeFileId);
      }
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const activeFile = activeFileId ? fileSystem.get(activeFileId) : null;
  const hasOpenFiles = openFiles.size > 0;

  // 创建右键菜单项
  const contextMenuItems: ItemType[] = [
    {
      key: 'newFile',
      label: '新建文件',
      icon: <FileAddOutlined />,
    },
    {
      key: 'newFolder',
      label: '新建文件夹',
      icon: <FolderAddOutlined />,
    },
    { type: 'divider' },
    {
      key: 'rename',
      label: '重命名',
      icon: <EditOutlined />,
      disabled: contextMenu.targetId === 'root',
    },
    {
      key: 'copy',
      label: '复制',
      icon: <CopyOutlined />,
      disabled: contextMenu.targetId === 'root',
    },
    {
      key: 'paste',
      label: '粘贴',
      icon: <ScissorOutlined />,
      disabled: !clipboard,
    },
    { type: 'divider' },
    {
      key: 'delete',
      label: '删除',
      icon: <DeleteOutlined />,
      danger: true,
      disabled: contextMenu.targetId === 'root',
    },
  ];

  return (
    <div className="code-editor" onClick={hideContextMenu}>
      <div className="editor-container">
        {/* 文件面板 */}
        <div className="file-panel" style={{ width: filePanelWidth }}>
          <div className="file-panel-header">
            <h3>文件资源管理器</h3>
            <div className="toolbar">
              <Space size="small">
                <Tooltip title="新建文件">
                  <FileAddOutlined onClick={() => showCreateModal('file', 'root')} />
                </Tooltip>
                <Tooltip title="新建文件夹">
                  <FolderAddOutlined onClick={() => showCreateModal('folder', 'root')} />
                </Tooltip>
              </Space>
            </div>
          </div>

          <FileTree
            fileSystem={fileSystem}
            onFileClick={openFile}
            onFolderToggle={toggleFolder}
            onContextMenu={handleContextMenu}
            activeFileId={activeFileId}
            openFiles={openFiles}
          />
        </div>

        {/* 拖拽分隔条 */}
        <div className="resizer" onMouseDown={handleMouseDown} />

        {/* 编辑器面板 */}
        <div className="editor-panel">
          {hasOpenFiles && (
            <TabBar
              openFiles={openFiles}
              fileSystem={fileSystem}
              activeFileId={activeFileId}
              onTabClick={setActiveFileId}
              onTabClose={closeFile}
            />
          )}
          <div className="monaco-container">
            {hasOpenFiles ? (
              <Monaco
                height="100%"
                theme="vs-dark"
                language={activeFile?.language || 'javascript'}
                // 移除 value 属性，完全依赖模型系统管理内容
                onMount={handleEditorMount}
                options={{
                  automaticLayout: true,
                  fontSize: 14,
                  wordWrap: 'on',
                  minimap: { enabled: true },
                  scrollBeyondLastLine: false,
                  renderLineHighlight: 'all',
                  selectOnLineNumbers: true,
                  tabSize: 2,
                  insertSpaces: true,
                }}
              />
            ) : (
              <div className="welcome-screen">
                <h2>简陋的代码编辑器</h2>
                <p>选择一个文件开始编辑，或创建一个新文件</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 使用Antd的Dropdown替代原来的ContextMenu */}
      {contextMenu.show && (
        <Dropdown
          open={contextMenu.show}
          menu={{
            items: contextMenuItems,
            onClick: ({ key }) => handleFileTreeAction(key, contextMenu.targetId),
          }}
          trigger={['contextMenu']}
          overlayStyle={{
            position: 'fixed',
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
          }}
        >
          <div style={{ position: 'fixed', left: contextMenu.x, top: contextMenu.y, width: 0, height: 0 }} />
        </Dropdown>
      )}

      {/* 创建文件/文件夹的Modal */}
      <Modal
        title={`新建${createModalType === 'file' ? '文件' : '文件夹'}`}
        open={createModalVisible}
        onOk={handleCreateItem}
        onCancel={() => setCreateModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <Input
          placeholder={`请输入${createModalType === 'file' ? '文件' : '文件夹'}名称`}
          value={createModalName}
          onChange={e => setCreateModalName(e.target.value)}
          autoFocus
        />
      </Modal>

      {/* 重命名Modal */}
      <Modal
        title="重命名"
        open={renameModalVisible}
        onOk={handleRename}
        onCancel={() => setRenameModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <Input
          placeholder="请输入新名称"
          value={renameModalName}
          onChange={e => setRenameModalName(e.target.value)}
          autoFocus
        />
      </Modal>

      {/* 删除确认Modal */}
      <Modal
        title="确认删除"
        open={deleteModalVisible}
        onOk={handleDelete}
        onCancel={() => setDeleteModalVisible(false)}
        okText="确定"
        cancelText="取消"
      >
        <p>
          确定要删除{deleteModalType === 'folder' ? '文件夹' : '文件'} "{deleteModalName}"
          {deleteModalType === 'folder' ? '及其所有内容' : ''}吗？
        </p>
      </Modal>
    </div>
  );
};

export default CodeEditor;
